import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DraggableYearlyMultiLineChart, {
  generateDefaultCurves,
} from '../../../components/utility_planning/DraggableYearlyMultiLineChart';
import { getUtilityWorkflowUiState, updateUtilityWorkflowUiState } from '../../../utils/utilityWorkflowUiState';

const DEFAULT_START_YEAR = 2026;
const DEFAULT_END_YEAR = 2050;

function PassengerEvZctaAdoptionTrendPage() {
  const { zcta } = useParams();
  const storedUiState = getUtilityWorkflowUiState().passengerZctaAdoptionTrend?.[zcta] || {};
    const [startYear, setStartYear] = useState(storedUiState.startYear ?? DEFAULT_START_YEAR);
  const [endYear, setEndYear] = useState(storedUiState.endYear ?? DEFAULT_END_YEAR);
  const [curves, setCurves] = useState(() =>
    generateDefaultCurves(DEFAULT_START_YEAR, DEFAULT_END_YEAR)
  );

  const startYearNum = Number(startYear) || DEFAULT_START_YEAR;
  const endYearNum = Number(endYear) || DEFAULT_END_YEAR;
  const validRange = startYearNum < endYearNum;

  useEffect(() => {
    if (!validRange) return;
    setCurves(generateDefaultCurves(startYearNum, endYearNum));
  }, [startYearNum, endYearNum, validRange]);

  useEffect(() => {
    const allZctaState = getUtilityWorkflowUiState().passengerZctaAdoptionTrend || {};
    updateUtilityWorkflowUiState('passengerZctaAdoptionTrend', {
      ...allZctaState,
      [zcta]: {
        startYear,
        endYear,
      },
    });
  }, [endYear, startYear, zcta]);

  const handleCurvesUpdate = useCallback((newCurves) => {
    setCurves(newCurves);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 font-ui">
        <div className="p-8 max-w-6xl mx-auto">
          <div className="mb-6">
            <Link
              to="/utility/workflow/system-models/passenger-ev/adoption-trend"
              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              ← Back to Utility Adoption Trend
            </Link>
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-2">ZCTA {zcta} — EV Adoption Trend</h1>
          <p className="text-gray-600 mb-8">
            Zip code–level adoption forecast. Drag points to adjust EV adoption % by year.
          </p>

          {/* Forecast year range */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-800 shrink-0">Forecast range</h2>
              <label className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Start year</span>
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={startYear}
                  onChange={(e) => setStartYear(e.target.value)}
                  className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">End year</span>
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={endYear}
                  onChange={(e) => setEndYear(e.target.value)}
                  className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </label>
            </div>
          </section>

          {/* ZCTA adoption curves - baseline only */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              ZCTA adoption forecasting
            </h2>
            {validRange ? (
              <DraggableYearlyMultiLineChart
                startYear={startYearNum}
                endYear={endYearNum}
                curves={curves}
                onCurvesUpdate={handleCurvesUpdate}
                xLabel="Year"
                yLabel="EV adoption (%)"
                height={360}
              />
            ) : (
              <p className="text-amber-600 py-4">Set start year &lt; end year to show the chart.</p>
            )}
          </section>
        </div>
    </div>
  );
}

export default PassengerEvZctaAdoptionTrendPage;
